import consumer from "./consumer"

document.addEventListener("DOMContentLoaded", setupAgentChat)
document.addEventListener("turbolinks:load", setupAgentChat)
document.addEventListener("turbolinks:before-cache", teardownAgentChat)
document.addEventListener("beforeunload", teardownAgentChat)

let subscription = null

function setupAgentChat() {
    const wrapper = document.querySelector('[data-channel-subscribe="agent_chat"]')
    if (!wrapper) return

    const userId = wrapper.dataset.user
    const conversationId = wrapper.dataset.conversationId
    const messagesEl = wrapper.querySelector('[data-chat-messages]')
    const emptyStateEl = wrapper.querySelector('[data-empty-state]')
    const typingIndicator = wrapper.querySelector('[data-typing-indicator]')
    const form = wrapper.querySelector('#agent-message-form')
    const textarea = wrapper.querySelector('#message-input')
    const submitBtn = form.querySelector('button[type="submit"]')
    const suggestionButtons = wrapper.querySelectorAll('[data-chat-suggestion]')

    const chatState = {
        streamBuffer: "",
        streamMessageEl: null,
        isStreaming: false,
        conversationId: conversationId
    }

    // Check for an existing resuming message element (created by inline script on page load)
    const resumingEl = messagesEl?.querySelector('[data-resuming="true"]');
    if (resumingEl) {
        chatState.streamMessageEl = resumingEl;
        chatState.streamBuffer = resumingEl.querySelector('[data-message-content]')?.innerHTML || "";
        chatState.isStreaming = true;
    }

    // Check for active job on page load and restore state if we don't already have a resuming element
    if (conversationId && !resumingEl) {
        checkAndResumeActiveJob(conversationId, chatState, messagesEl, emptyStateEl, typingIndicator)
    }

    subscription = consumer.subscriptions.create(
        { channel: "AgentChatChannel", user_id: userId },
        {
            connected() {
                // Only hide typing indicator if we're not already streaming
                if (!chatState.isStreaming) {
                    typingIndicator?.classList.remove('is-visible')
                    messagesEl?.setAttribute('aria-busy', 'false')
                }
                
                // Re-check for active job when reconnecting (only if we don't already have a stream)
                if (chatState.conversationId && !chatState.streamMessageEl) {
                    checkAndResumeActiveJob(chatState.conversationId, chatState, messagesEl, emptyStateEl, typingIndicator)
                }
            },
            received(data) {
                if (!messagesEl) return

                if (data.error) {
                    addMessage({
                        role: 'agent',
                        content: `<strong>Something went wrong.</strong><br>${escapeHTML(data.error)}`,
                        isError: true
                    })
                    typingIndicator?.classList.remove('is-visible')
                    messagesEl.setAttribute('aria-busy', 'false')
                    chatState.streamBuffer = ""
                    chatState.streamMessageEl = null
                    chatState.isStreaming = false
                    return
                }

                if (data.token) {
                    handleStreamingToken(data.token)
                }

                if (data.done) {
                    finalizeStream()
                }
            }
        }
    )

    function handleStreamingToken(token) {
        if (!messagesEl) return

        if (!chatState.streamMessageEl) {
            // Check for an existing resuming element first
            const resumingEl = messagesEl.querySelector('[data-resuming="true"]');
            if (resumingEl) {
                chatState.streamMessageEl = resumingEl;
                chatState.streamBuffer = resumingEl.querySelector('[data-message-content]')?.innerHTML || "";
            } else {
                chatState.streamMessageEl = addMessage({ role: 'agent', streaming: true })
                chatState.streamBuffer = ""
            }
        }

        chatState.streamBuffer += token
        chatState.isStreaming = true
        showTypingIndicator()

        const contentEl = chatState.streamMessageEl.querySelector('[data-message-content]')
        if (contentEl) {
            contentEl.innerHTML = sanitizeHTML(chatState.streamBuffer)
        }
    }

    function finalizeStream() {
        if (!chatState.streamMessageEl) return
        chatState.streamMessageEl.classList.remove('is-streaming')
        chatState.streamMessageEl.removeAttribute('data-resuming')
        const contentEl = chatState.streamMessageEl.querySelector('[data-message-content]')
        if (contentEl) {
            contentEl.innerHTML = sanitizeHTML(chatState.streamBuffer.trim())
        }
        typingIndicator?.classList.remove('is-visible')
        messagesEl?.setAttribute('aria-busy', 'false')
        scrollMessagesToBottom()
        chatState.streamBuffer = ""
        chatState.streamMessageEl = null
        chatState.isStreaming = false
    }

    function addMessage({ role, content = "", streaming = false, isError = false }) {
        if (!messagesEl) return null
        emptyStateEl?.classList.add('d-none')

        const messageEl = document.createElement('div')
        messageEl.classList.add('chat-message', `from-${role}`)
        if (streaming) messageEl.classList.add('is-streaming')
        if (isError) messageEl.classList.add('is-error')

        const initials = role === 'user' ? (wrapper.dataset.initials || 'You') : 'AI'

        messageEl.innerHTML = `
            <div class="message-avatar" aria-hidden="true">${initials}</div>
            <div class="message-body">
                <div class="message-meta">
                    <span>${role === 'user' ? 'You' : 'Tangram Agent'}</span>
                    <span>${new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                </div>
                <div class="message-content" data-message-content>${content}</div>
            </div>
        `

        messagesEl.appendChild(messageEl)
        scrollMessagesToBottom()
        return messageEl
    }

    function showTypingIndicator() {
        typingIndicator?.classList.add('is-visible')
        messagesEl?.setAttribute('aria-busy', 'true')
        scrollMessagesToBottom()
    }

    function scrollMessagesToBottom() {
        if (!messagesEl) return
        messagesEl.scrollTop = messagesEl.scrollHeight
    }

    function sanitizeHTML(html) {
        const template = document.createElement('template')
        template.innerHTML = html
        template.content.querySelectorAll('script').forEach((script) => script.remove())
        template.content.querySelectorAll('*').forEach((el) => {
            Array.from(el.attributes).forEach((attr) => {
                if (/^on/i.test(attr.name)) {
                    el.removeAttribute(attr.name)
                }
            })
        })
        return template.innerHTML
    }

    function escapeHTML(str) {
        const div = document.createElement('div')
        div.textContent = str
        return div.innerHTML
    }

    function handleSubmit(event) {
        event.preventDefault()
        const message = textarea.value.trim()
        if (!message) return

        addMessage({
            role: 'user',
            content: escapeHTML(message)
        })

        textarea.value = ""
        textarea.dispatchEvent(new Event('input'))
        textarea.focus()

        showTypingIndicator()
        messagesEl?.setAttribute('aria-busy', 'true')

        // Include conversation_id if we have one
        const postData = { message: message }
        if (chatState.conversationId) {
            postData.conversation_id = chatState.conversationId
        }

        // Get CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        
        fetch(form.getAttribute('action'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
                'Accept': 'application/json'
            },
            body: JSON.stringify(postData)
        })
        .then(response => response.json())
        .then(response => {
            // Update conversation ID if we got one back
            if (response.conversation_id && !chatState.conversationId) {
                chatState.conversationId = response.conversation_id
                wrapper.dataset.conversationId = response.conversation_id
                
                // Update URL so refresh will load the same conversation
                const newUrl = new URL(window.location.href)
                newUrl.searchParams.set('conversation_id', response.conversation_id)
                window.history.replaceState({}, '', newUrl.toString())
            }
        })
        .catch(err => {
            console.error('Error sending message:', err)
        })
    }

    function handleInput() {
        const hasText = textarea.value.trim().length > 0
        submitBtn.disabled = !hasText
    }

    function handleKeydown(event) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            handleSubmit(event)
        }
    }

    function handleSuggestionClick(event) {
        const suggestion = event.currentTarget.dataset.chatSuggestion
        if (!suggestion) return
        textarea.value = suggestion
        textarea.dispatchEvent(new Event('input'))
        textarea.focus()
    }

    form.addEventListener('submit', handleSubmit)
    textarea.addEventListener('input', handleInput)
    textarea.addEventListener('keydown', handleKeydown)
    suggestionButtons.forEach((btn) => {
        btn.addEventListener('click', handleSuggestionClick)
    })

    wrapper.__agentChatCleanup = () => {
        form.removeEventListener('submit', handleSubmit)
        textarea.removeEventListener('input', handleInput)
        textarea.removeEventListener('keydown', handleKeydown)
        suggestionButtons.forEach((btn) => {
            btn.removeEventListener('click', handleSuggestionClick)
        })
    }
    
    // Helper to check for active job and resume streaming
    function checkAndResumeActiveJob(convId, state, msgEl, emptyEl, typingEl) {
        fetch(`/agent/conversations/${convId}/status`)
            .then(response => response.json())
            .then(data => {
                if (data.active) {
                    // There's an active job - resume the stream
                    state.isStreaming = true
                    
                    // Show typing indicator
                    typingEl?.classList.add('is-visible')
                    msgEl?.setAttribute('aria-busy', 'true')
                    
                    // Only create message element and show buffer if we have content
                    if (data.buffer && data.buffer.length > 0) {
                        emptyEl?.classList.add('d-none')
                        
                        // Create a streaming message element if we don't have one
                        if (!state.streamMessageEl) {
                            state.streamMessageEl = addMessage({ role: 'agent', streaming: true })
                        }
                        
                        // Set the buffer and display it
                        state.streamBuffer = data.buffer
                        
                        const contentEl = state.streamMessageEl.querySelector('[data-message-content]')
                        if (contentEl) {
                            contentEl.innerHTML = sanitizeHTML(state.streamBuffer)
                        }
                        
                        scrollMessagesToBottom()
                    }
                }
            })
            .catch(err => {
                console.error('Error checking job status:', err)
            })
    }
}

function teardownAgentChat() {
    const wrapper = document.querySelector('[data-channel-subscribe="agent_chat"]')
    if (wrapper && typeof wrapper.__agentChatCleanup === 'function') {
        wrapper.__agentChatCleanup()
    }

    if (subscription) {
        consumer.subscriptions.remove(subscription)
        subscription = null
    }
}